import {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {logout} from '@api/auth.js'
import {getAuthData} from '@utils/auth.js'
import {broadcastAuthUpdate, clearAuthSession} from '@utils/authSession.js'

const EMPTY_SESSION = {token: null, expiresAt: null}
const MAX_TIMEOUT_MS = 2_147_483_647
const SESSION_LOGOUT_SKEW_MS = 5_000

function normalizeSession(rawSession) {
  const token = typeof rawSession?.token === 'string' && rawSession.token.trim() ? rawSession.token : null
  const expiresAtValue = rawSession?.expiresAt
  const expiresAt = typeof expiresAtValue === 'number' ? expiresAtValue : Number(expiresAtValue)

  if (!token || !Number.isFinite(expiresAt)) {
    return {...EMPTY_SESSION}
  }

  return {token, expiresAt}
}

function readStoredSession() {
  if (typeof window === 'undefined') {
    return {...EMPTY_SESSION}
  }

  try {
    const data = getAuthData()
    return normalizeSession(data)
  } catch {
    return {...EMPTY_SESSION}
  }
}

export default function useAuthSessionWatcher() {
  const navigate = useNavigate()
  const [session, setSession] = useState(() => readStoredSession())
  const timerRef = useRef(null)
  const signingOutRef = useRef(false)
  const latestSessionRef = useRef(session)
  const previousTokenRef = useRef(session.token)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const finalizeSignOut = useCallback(() => {
    clearTimer()
    clearAuthSession({broadcast: false})
    broadcastAuthUpdate()

    setSession({...EMPTY_SESSION})
    signingOutRef.current = false
    navigate('/login', {replace: true})
  }, [clearTimer, navigate])

  const handleAutoLogout = useCallback(async () => {
    if (signingOutRef.current) return
    signingOutRef.current = true

    try {
      await logout({reason: 'auto'})
    } catch (error) {
      console.warn('[auth] Automatic logout request error', error)
    } finally {
      finalizeSignOut()
    }
  }, [finalizeSignOut])

  const scheduleLogoutCheck = useCallback(() => {
    if (typeof window === 'undefined') return

    const currentSession = latestSessionRef.current

    if (!currentSession.token || !currentSession.expiresAt) {
      return
    }

    const remainingLifetime = currentSession.expiresAt - Date.now() - SESSION_LOGOUT_SKEW_MS

    if (remainingLifetime <= 0) {
      handleAutoLogout()
      return
    }

    const delay = Math.min(remainingLifetime, MAX_TIMEOUT_MS)

    timerRef.current = window.setTimeout(() => {
      scheduleLogoutCheck()
    }, delay)
  }, [handleAutoLogout])

  const syncWithStorage = useCallback(() => {
    if (typeof window === 'undefined') return

    setSession(previous => {
      const next = readStoredSession()

      if (next.token && next.expiresAt) {
        signingOutRef.current = false
      }

      if (previous.token === next.token && previous.expiresAt === next.expiresAt) {
        return previous
      }

      return next
    })
  }, [])

  useEffect(() => {
    latestSessionRef.current = session
  }, [session])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleStorage = event => {
      if (event && event.key && event.key !== 'auth') return
      syncWithStorage()
    }

    const handleFocus = () => syncWithStorage()
    const handleVisibility = () => {
      if (typeof document === 'undefined') return
      if (document.visibilityState === 'visible') {
        syncWithStorage()
      }
    }

    const handleAuthUpdate = () => syncWithStorage()

    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', handleFocus)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility)
    }
    window.addEventListener('auth:updated', handleAuthUpdate)
    window.addEventListener('auth-updated', handleAuthUpdate)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', handleFocus)
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility)
      }
      window.removeEventListener('auth:updated', handleAuthUpdate)
      window.removeEventListener('auth-updated', handleAuthUpdate)
    }
  }, [syncWithStorage])

  useEffect(() => {
    const previousToken = previousTokenRef.current
    previousTokenRef.current = session.token

    if (!session.token && previousToken) {
      navigate('/login', {replace: true})
    }
  }, [session.token, navigate])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    clearTimer()

    if (!session.token || !session.expiresAt) {
      return undefined
    }

    scheduleLogoutCheck()

    return () => {
      clearTimer()
    }
  }, [session.token, session.expiresAt, scheduleLogoutCheck, clearTimer])

  useEffect(() => {
    return () => {
      clearTimer()
    }
  }, [clearTimer])
}
